import PlaneRestraint from "./PlaneRestraint";
import {point, vector, Polygon, BooleanOperations, matrix} from "@flatten-js/core";
import convexHull from 'monotone-convex-hull-2d';
import {v2, v3} from "oc-threejs/short3";
import Scenes from "oc-threejs/Scenes";
import Maths3 from "oc-threejs/Maths3";
import Commons from "oc-commons/Commons";
import PlaneIntersectionHandler from "oc-threejs/collision/PlaneIntersectionHandler";
import simplify from 'simplify-js';
import UniversalLoader from "oc-threejs/loader/UniversalLoader";
import * as clipperLib from "js-angusj-clipper/web";

// create an instance of the library (usually only do this once in your app)
let clipper = null;
clipperLib.loadNativeClipperLibInstanceAsync(
    // let it autodetect which one to use, but also available WasmOnly and AsmJsOnly
    clipperLib.NativeClipperLibRequestedFormat.WasmWithAsmJsFallback
).then(result=>clipper=result);

const borderLineMaterial = new THREE.LineBasicMaterial( { color: 0xff0000 } );
const sliceMeshMaterial = new THREE.LineBasicMaterial( { color: 0xffff00, lineWidth: 1 } );

// Erkenntnisse:
// 1. Bei komplexen Geometrien ist es ein Performancekiller, eine konvexe Hülle mit jedem Punkt einer
//    anderen konvexen Hülle zu erweitern. Die Hüll-Polygone müssen radikal reduziert werden.
//    Alternativ braucht es einen Algorithmus, der ein Polygon um ein anderes in der Art eines
//    Pinsels erweitert.
// 2. Polygon-Union ist sehr teuer, daher clipper-lib notwendig
//


/**
 * Ein Restraint, der eine Kollisionserkennung macht und Objekte in der Ebene um Hindernisse herum
 * dragbar macht. Dabei werden die Objekte auf die Ebene projeziert, und es wird ein Polygon des
 * erlaubten Dragbereiches gebildet. Damit trotzdem Stühle unter Tische gestellt werden können,
 * können Hähenbereiche angegeben werden, die bei der Kollisionserkennung separat betrachtet werden.
 */
export class GroundBox2dCollisionRestraint extends PlaneRestraint {

    debugVisualizations = [];

    constructor(options) {
        super({
            ...options,
            plane: Maths3.plane.y0,
        });
        // collisionObjects sind die statischen Objekte, gegen die Collisionen geprüft werden
        this.collisionObjectsUnfiltered = options.collisionObjects ?? [];
        // collisionHeightIntervalls sind optionale Höhenbereiche, für die separate Boundingboxes ermittelt werden.
        // z.B. [0.5] schneidet alle Objekte in jeweils zwei Höhenbereiche, eine Box für den Höhenbereich Höhe von -oo bis 0.5
        // und eine Box in der Höhe von 0.5 bis +oo. Die BoundingBoxes werden über die im Höhenbereich enthaltenen Vertices sowie
        // das Schnittpolygon mit der jeweils oberen und unteren Schnittebene ermittelt (soweit nicht unendlich). Dadurch ist sichergestellt, dass
        // die BoundingBoxen für die Höhenbereiche wirklich korrekt sind.
        this.collisionHeightIntervalls = _.uniq(
            (options.collisionHeightIntervalls ?? [])
            .concat([Number.NEGATIVE_INFINITY,Number.POSITIVE_INFINITY])
            .sort()
        );
        // zusätzlicher Abstand von anderen Objekten
        // ACHTUNG: kann die Performance bei großen Polygonen beeinträchtigen
        this.offset = options.offset ?? 0;
        this.polygonSimplifyTollerance = options.polygonSimplifyTollerance ?? 0.05;
        this.showDebugVisualization = options.showDebugVisualization ?? false;
    }

    updateCollisionObjects(objects) {
        this.collisionObjectsUnfiltered = objects || [];
    }

    preparePolygon() {
        this.collisionObjects = this.collisionObjectsUnfiltered.filter(o=>o!==this.dragObject3d);

        console.time("GroundBox2dCollisionRestraint.onDrag3dBegin");

        STAGE.scene.updateMatrixWorld();

        // Die Objekte werden auf die Ebene projeziert, und daraus werden Polygone erzeugt.
        // Dann kommt allerdings ein Trick: Gesucht ist ja eigentlich die Menge aller erlaubten Positionen des gedraggten Objektes,
        // und das ist wiederum ein Polygon, und zwar eines, das den projezierten Polygonen der anderen Objekte,
        // erweitert um das Polygon des gedraggten Objektes, entspricht. Leider habe ich nirgends einen Algorithmus gefunden,
        // wie man ein Polygon um ein anderes Polygon erweitert, also sozusagend die Kontur des einen Polygons
        // mit dem anderen Polygon als Pinsel nachzeichnet. Statt werden die Konvexen Hüllen der Eckpunkte des
        // Polygons gebildet, um welche die Eckpunkte des anderen Polygons gesetzt werden. Dan
        // Dann muss man nur noch das Abstandsegment vom Maus-Punkt auf der Ebene zu diesem Multipolygon ermitteln,
        // und ob man innen oder außen ist. Zum Glück bietet flatten.js alles das an, bis auf die konvexe Hülle.


        const pos = this.dragObject3d.position.clone();
        // const dragPolygonSlices = this.object3dToPolygon2dPerHeightSlice(this.dragObject3d)
        //         .map(polygon=>polygon?.translate(this.v3ToPoint(pos.negate())));


        // const dragMatrix = matrix()
        //     .rotate(180*Math.PI/180)
        //     .translate(-pos.x,-pos.z);
        // .scale(-1,0,-1)
        //
        const dragHullSlices = this.object3dToHull2dPerHeightSlice(this.dragObject3d)
            .map(hull=>hull?.map(p=>([-(p[0]-pos.x),-(p[1]-pos.z)])));

        // .map(polygon=>polygon.translate(
        //     this.v3ToPoint(
        //         this.dragObject3d.position.clone().negate())));


        const drawHullsAround = (a, b) => {
            if (a==null) {
                return b;
            }
            if (b==null) {
                return null;
            }
            // nur für konvexe Polygone
            const cloud = a.flatMap(v => b.map(w => [v[0]+w[0],v[1]+w[1]]));
            return this.simplify(this.convexHull(cloud));
        }

        const hindernissHullSlices = this.collisionObjects.map(
            o => this.object3dToHull2dPerHeightSlice(o)
        );

        const widenedHindernissHulls = hindernissHullSlices.map(
            hullslices => hullslices.map(
                    (hull,i) =>
                        drawHullsAround(dragHullSlices[i], hull)
            )
        );

        this.forbiddenPolygon = this.hullsToPolygon(this.unionHulls(
            widenedHindernissHulls.flatMap(x=>x)
        ));

        // if (this.offset>0 && this.forbiddenPolygon) {
        //     this.forbiddenPolygon = offset(this.forbiddenPolygon, this.offset);
        // }

        console.timeEnd("GroundBox2dCollisionRestraint.onDrag3dBegin");

        this.showDebugObjects({hindernissHullSlices,dragHullSlices});
    }

    onDrag3dBegin() {
        super.onDrag3dBegin();
        this.preparePolygon();
        UniversalLoader.delayUntilEverythingLoaded(()=>this.preparePolygon());
    }

    onDrag3dEnd() {
        this.hideDebugObjects();
    }


    // checkCollision() {
    //     // dragObject3d muss gesetzt sein + Constructor-Parameter
    //     this.onDrag3dBegin();
    //     const fp = this.dragObject3d.position.clone().setY(0);
    //     return this.forbiddenPolygon?.contains(fp) ?? false;
    // }

    checkCollision() {
        let collision = false;
        if (this.dragObject3d) {
            this.preparePolygon();
            const fp = this.v3ToPoint(this.dragObject3d.position.clone().setY(0));
            collision = this.forbiddenPolygon?.contains(fp) ?? false;
        }
        return collision;
    }

    raycastLocal(ray) {
        const p = super.raycastLocal(ray);
        if (p==null) {
            return null;
        }
        if (this.forbiddenPolygon==null) {
            return p;
        }
        const fp = this.v3ToPoint(p);
        const [distance,distanceSegment] = fp.distanceTo(this.forbiddenPolygon);
        if (this.forbiddenPolygon?.contains(fp)) {
            return this.pointToV3(distanceSegment.end);
        } else {
            this.showLine(distanceSegment);
            return p;
        }
    }

    object3dToHull2dPerHeightSlice(object3d) {

        // bilde die konvexe Hülle jeder Plane auf Höhe y des collisionHeightIntervalls
        // und erzeige eine Datenstruktur {y,vertices:[]}
        // PlaneIntersectionHandler liefert Weltkoordinaten
        const ys = this.collisionHeightIntervalls;
        const vertices = ys.map(()=>[]);

        const findYIndex = y => {
            let i=0;
            while (ys[i]<y) i++;
            return i;
        }

        const add = (yi,x,z) => {
            // vertices[yi-1].push([x,z]);
            vertices[yi].push([x,z]);
        }

        // trage alle Eckpunkte und die Schnittpunkte der Kanten mit den Schnittebenen
        // in die Hüllpunkte der entsprechenden Schnittebene ein
        // traverseEdges liefert Weltkoordinaten
        Scenes.traverseEdges(object3d,(a,b)=>{
            // sortiere y asc
            if (a.y>b.y) {
                let h = a;
                a = b;
                b = h;
            }
            const ai = findYIndex(a.y);
            const bi = findYIndex(b.y);
            add(ai,a.x,a.z);
            add(bi,b.x,b.z);
            const dy = b.y-a.y;
            const dx = b.x-a.x;
            const dz = b.z-a.z;
            for (let i=ai;i<bi;i++) {
                let y = (ys[i]-a.y) / dy;
                add(i,a.x+dx*y,a.z+dz*y);
            }
        });

        const hulls = vertices.map(vs=>this.simplify(this.convexHull(vs)));

        return hulls;
    }

    unionHulls(hulls) {
        // hull: [[x,y],..]
        const scale = 1e10;
        const floatToInt = x=>Math.floor(scale*x);
        const intToFloat = x=>x/scale;
        const arrayToIntPoint = ([x,y])=>({x:floatToInt(x),y:floatToInt(y)});
        const intPointToArray = ({x,y})=>[intToFloat(x),intToFloat(y)];
        const hullToPath = hull=>clipper.cleanPolygon(hull.map(arrayToIntPoint));
        const paths = hulls.filter(p=>p!=null).map(hullToPath).filter(path=>Math.abs(clipper.area(path))>0.1);
        if (paths.length==0) {
            return null;
        }
        if (paths.length==1) {
            return paths[0].map(intPointToArray);
        }
        // console.log('paths',paths);
        const polyResults = clipper.clipToPaths({
            clipType: clipperLib.ClipType.Union,
            subjectInputs: paths.map(data=>({ data, closed: true })),
            subjectFillType: clipperLib.PolyFillType.NonZero,
            strictlySimple: true,
        });
        const hullResults = polyResults.map(poly=>poly.map(intPointToArray));
        return hullResults;
    }


    // unionPolygons(polygons) {
    //     const filtered = polygons.filter(p=>p!=null);
    //     if (filtered.length==0) {
    //         return null;
    //     }
    //     if (filtered.length==1) {
    //         return filtered[0];
    //     }
    //     // https://github.com/alexbol99/flatten-js/issues/94
    //     const scale = 100000;
    //     const scaleMulE6 = matrix().scale(scale, scale);
    //     const scaleDivE6 = matrix().scale(1/scale, 1/scale);
    //     const union = filtered
    //         .map(p=>p.transform(scaleMulE6))
    //         .reduce((a, b) => {
    //             if (a.faces.first().orientation() != b.faces.first().orientation()) {
    //                 try {
    //                     b=b.reverse();
    //                 } catch (e) {
    //                     try {
    //                         a=a.reverse();
    //                     } catch (e) {
    //                         console.warn(e);
    //                     }
    //                 }
    //             }
    //             try {
    //                 return BooleanOperations.unify(a, b);
    //             } catch (e) {
    //                 console.warn(e);
    //                 return a;
    //             }
    //         }, filtered[0]);
    //     return union.transform(scaleDivE6);
    // }

    convexHull(arrayPoints) {
        if (arrayPoints==null || arrayPoints.length==0) {
            return null;
        }
        // alternativ könnte man https://github.com/mapbox/concaveman probieren
        const hull = convexHull(arrayPoints).map(i => arrayPoints[i]);
        if (hull.length==0) {
            return null;
        }
        return hull;
    }

    simplify(arrayPoints) {
        if (arrayPoints==null) {
            return null;
        }
        if (arrayPoints.length<10) {
            return arrayPoints;
        }
        const points = arrayPoints.map(p=>({x:p[0],y:p[1]}));
        const result = simplify(points,this.polygonSimplifyTollerance,true);
        return result.map(p=>([p.x,p.y]));
    }

    hullToPolygon(arrayPoints) {
        if (arrayPoints==null) {
            return null;
        }
        return new Polygon(arrayPoints.map(([x, y]) => point(x, y)));
    }

    hullsToPolygon(arrayOfArrayPoints) {
        arrayOfArrayPoints = arrayOfArrayPoints?.filter(a=>a!=null && a.length!=0) || [];
        return new Polygon(arrayOfArrayPoints.map(array=>array.map(([x, y]) => point(x, y))));
    }

    v3ToPoint(v) {
        return point(v.x,v.z)
    }

    pointToV3(p) {
        return v3(p.x,0,p.y);
    }

    // nur für Demozwecke ---------------------------------

    showDebugObjects({hindernissHullSlices,dragHullSlices}) {
        if (!this.showDebugVisualization) {
            return;
        }

        STAGE.setSecondRenderPass(true);

        hindernissHullSlices.forEach(slices=>slices.forEach((hull,i)=>{
            if (hull==null) {
                return;
            }
            const p = this.hullToPolygon(hull);
            let height = this.collisionHeightIntervalls[i];
            height = height==Number.NEGATIVE_INFINITY ? -1 :
                height==Number.POSITIVE_INFINITY ? +3 : height;
            const mesh = this.polygonToMesh(p,sliceMeshMaterial);
            mesh.position.setY(height);
            STAGE.secondPassScene.add(mesh);
            this.debugVisualizations.push(mesh);
        }));

        dragHullSlices.forEach((hull,i)=>{
            if (hull==null) {
                return;
            }
            const p = this.hullToPolygon(hull);
            let height = this.collisionHeightIntervalls[i];
            height = height==Number.NEGATIVE_INFINITY ? -1 :
                height==Number.POSITIVE_INFINITY ? +3 : height;
            const mesh = this.polygonToMesh(p,sliceMeshMaterial);
            mesh.position.setY(height).setX(this.dragObject3d.position.x).setZ(this.dragObject3d.position.z);
            STAGE.secondPassScene.add(mesh);
            // this.dragObject3d.add(mesh);
            this.debugVisualizations.push(mesh);
        });

        if (!this.forbiddenPolygon) {
            return;
        }

        const forbiddenMesh = this.polygonToMesh(this.forbiddenPolygon,borderLineMaterial);
        this.debugVisualizations.push(forbiddenMesh);
        STAGE.secondPassScene.add(forbiddenMesh);

        const geometry = new THREE.BufferGeometry().setFromPoints( [v3(0,-this.dragObject3d.position.y,0),v3(0,10,0)] );
        const dragHandleZeroAxis = new THREE.Line( geometry, borderLineMaterial );
        this.dragObject3d.add(dragHandleZeroAxis);
        this.debugVisualizations.push(dragHandleZeroAxis);

        if (this.dragObject3d.anchor) {
            const anchorHelper = new THREE.AxisHelper(1);
            this.dragObject3d.anchor.add(anchorHelper);
            this.debugVisualizations.push(anchorHelper);
        }
    }

    hideDebugObjects() {
        if (!this.showDebugVisualization) {
            return;
        }
        this.debugVisualizations.forEach(o=>Scenes.remove(o));
        this.debugVisualizations = [];
        Scenes.remove(this.distLine3D);
        this.distLine3D = null;
    }

    showLine(distanceSegment) {
        if (!this.showDebugVisualization) {
            return;
        }
        Scenes.remove(this.distLine3D);
        const geometry = new THREE.BufferGeometry().setFromPoints( [distanceSegment.start,distanceSegment.end].map(p=>this.pointToV3(p)) );
        this.distLine3D = new THREE.Line( geometry, new THREE.LineBasicMaterial( { color: 0xff0000 } ) );
        STAGE.scene.add(this.distLine3D);
    }

    polygonToMesh(polygon,material) {
        const group = new THREE.Group();
        polygon.toArray().forEach(closedPolygon=>{
            const shape = new THREE.Shape();
            const vs = closedPolygon.vertices;
            if (vs.length==0) {
                return;
            }
            const moveTo = (p)=>shape.moveTo( p.x, p.y );
            const lineTo = (p)=>shape.lineTo( p.x, p.y );
            vs.forEach((p,i)=>(i==0?moveTo:lineTo)(p));
            lineTo(vs[0]);
            // const geometry = new THREE.ShapeGeometry( shape );
            // const material = new THREE.MeshStandardMaterial( { color: 0xff0000, side: THREE.DoubleSide, transparent: true, opacity: 0.1 } );
            // const mesh = new THREE.Mesh( geometry, material ) ;

            const points = shape.getPoints();
            const geometryPoints = new THREE.BufferGeometry().setFromPoints( points );
            const mesh = new THREE.Line( geometryPoints, material );

            mesh.quaternion.copy(Maths3.quaternion.x90);
            group.add(mesh);
        });
        return group;
    }

}
